import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import * as AuthSelectors from '../../store/auth.selectors';
import { selectAuthError, selectAuthLoading } from '../../store/auth.selectors';
import * as AuthActions from '../../store/auth.actions';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  FooterConfig,
  LayoutConfigService,
  UnauthenticatedLayoutComponent,
} from '@angular-monorepo/ui';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { PasswordModule } from 'primeng/password';
import { I18N_FACADE, I18nComponent, I18nFacade } from '@angular-monorepo/i18n';
import { MessageService } from 'primeng/api';
import { OAuthAuthService } from '../../data-access/oauth-auth.service';

@Component({
  selector: 'lib-login',
  imports: [
    CommonModule,
    TranslatePipe,
    ReactiveFormsModule,
    FloatLabel,
    InputText,
    Button,
    UnauthenticatedLayoutComponent,
    RouterLink,
    PasswordModule,
    I18nComponent,
    NgOptimizedImage,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit, OnDestroy {
  @Input() title?: string;
  @Input() backgroundImageUrl?: string;
  @Input() overlayClass = 'bg-black/40';
  @Input() headerClass = '';
  @Input() titleClass = 'text-gray-900';
  @Input() subtitleClass = 'text-gray-500 dark:text-gray-400';
  @Input() loginType: 'email' | 'ruc' = 'email';
  showPassword = false;
  hideForgotPassword = false;
  hideAzureLogin = false;
  hideGoogleLogin = true;
  hideMicrosoftLogin = true;
  hideRegister = false;
  hideI18n: boolean | undefined = false;

  form!: FormGroup;
  footerConfig: FooterConfig = { showDefault: true };
  variant: 'select' | 'toggle' | 'switch' = 'switch';
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;
  private destroy$ = new Subject<void>();
  private readonly formBuilder = inject(FormBuilder);
  private readonly layoutConfigService = inject(LayoutConfigService);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject<I18nFacade>(I18N_FACADE);
  private readonly oauthAuthService = inject(OAuthAuthService, { optional: true });

  constructor() {
    this.form = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(3)]],
    });
    this.loading$ = this.store.select(selectAuthLoading);
    this.error$ = this.store.select(selectAuthError);
  }

  ngOnInit() {
    this.initForm();
    this.loading$ = this.store.select(AuthSelectors.selectAuthLoading);

    // Verificar si la sesión expiró
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['sessionExpired'] === 'true') {
        this.messageService.add({
          severity: 'warn',
          summary: this.i18n.instant('auth.sessionExpired.title'),
          detail: this.i18n.instant('auth.sessionExpired.detail'),
          life: 6000,
        });
      }
      if (params['error']) {
        setTimeout(() => {
          this.messageService.add({
            severity: 'error',
            summary: this.i18n.instant('auth.socialError.title'),
            detail: this.i18n.instant(`auth.socialError.${params['error']}`),
            life: 6000,
          });
        }, 300);
      }
    });

    this.error$.pipe(takeUntil(this.destroy$)).subscribe((error) => {
      if (error) {
        console.log('💡 LoginComponent - Error en store:', error);
        // Mostrar toast de error
        this.messageService.add({
          severity: 'error',
          summary: this.i18n.instant('auth.errors.title') || 'Error de autenticación',
          detail: error,
          life: 5000,
        });
      }
    });

    this.layoutConfigService.footerConfig$.pipe(takeUntil(this.destroy$)).subscribe((config) => {
      this.footerConfig = config;

      if (config && config.customElements) {
        const i18nConfig = config.customElements.find((el) => el.content === 'lib-i18n');

        if (i18nConfig && i18nConfig.props?.variant) {
          this.variant = i18nConfig.props.variant as 'select' | 'toggle' | 'switch';
        }
      }

      // Config para ocultar links
      this.hideI18n = config?.hideI18n;
      this.hideForgotPassword = config?.hideForgotPasswordLink ?? false;
      this.hideAzureLogin = config?.hideAzureLoginButton ?? !this.oauthAuthService;
      this.hideGoogleLogin = config?.hideGoogleLoginButton ?? true;
      this.hideMicrosoftLogin = config?.hideMicrosoftLoginButton ?? true;
      this.hideRegister = config?.hideRegisterLink ?? false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    if (this.form.valid) {
      const credentials = this.form.value;
      this.store.dispatch(AuthActions.login({ credentials }));
    }
  }

  // Ejemplo con authService local storage
  /*onSubmitLogin() {
    this.authService
      .login(this.form.value.email, this.form.value.password)
      .subscribe((success) => {
        if (success) {
          console.log('Login exitoso');
          this.router.navigate(['/dashboard']).then((r) => r);
        } else {
          console.log('Credenciales inválidas');
          // Puedes mostrar un toast o mensaje de error
        }
      });
  }*/

  getEmailError(): string {
    const control = this.form.get('email');
    if (control?.hasError('required')) return this.i18n.instant('auth.errors.required');
    if (control?.hasError('email')) return this.i18n.instant('auth.errors.email');
    if (control?.hasError('minlength')) return this.i18n.instant('auth.errors.minlength');
    return '';
  }

  getPasswordError(): string {
    const control = this.form.get('password');
    if (control?.hasError('required')) return this.i18n.instant('auth.errors.required');
    if (control?.hasError('minlength')) return this.i18n.instant('auth.errors.minlength');
    return '';
  }

  /**
   * Inicia el flujo de autenticación OAuth con Azure/Cognito
   */
  onAzureLogin(): void {
    if (!this.oauthAuthService) {
      return;
    }
    // Guardar la URL de destino (dashboard por defecto)
    const targetUrl = '/dashboard';
    this.oauthAuthService.login(targetUrl);
  }

  /**
   * Redirige al login de Google
   */
  onGoogleLogin(): void {
    window.location.href = 'https://ditrack.ditech.es/accounts/google/login/';
  }

  /**
   * Redirige al login de Microsoft
   */
  onMicrosoftLogin(): void {
    window.location.href = 'https://ditrack.ditech.es/accounts/microsoft/login/';
  }

  private initForm(): void {
    const emailValidators =
      this.loginType === 'ruc'
        ? [Validators.required]
        : [Validators.required, Validators.email, Validators.minLength(3)];

    this.form = this.formBuilder.group({
      email: ['', emailValidators],
      password: ['', [Validators.required, Validators.minLength(3)]],
    });
  }
}
